import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { User, AuthState } from '@/types';
import { googleAuthService } from '@/services/auth/googleAuth';
import { tokenManager } from '@/services/auth/tokenManager';
import { sheetsService } from '@/services/sheets/sheetsService';

interface AuthContextType extends AuthState {
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Mensaje que ve quien NO esta en la lista de Autorizados (pestana del Registro
// Maestro). El candado de verdad vive en el Apps Script; aqui solo mostramos un
// mensaje amable y lo regresamos al login.
const MENSAJE_NO_AUTORIZADO =
  'Tu cuenta no está autorizada para usar esta aplicación. Si crees que es un error, contacta a tu administrador.';

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(initialState);

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        const tokens = await tokenManager.getTokens();
        if (tokens && !tokenManager.isTokenExpired(tokens)) {
          const user = await tokenManager.getUser();
          if (user) {
            setState({
              user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
            return;
          }
        }
        // No valid session
        setState((prev) => ({ ...prev, isLoading: false }));
      } catch (error) {
        console.error('Auth init error:', error);
        setState((prev) => ({ ...prev, isLoading: false, error: 'Error al inicializar autenticación' }));
      }
    };

    initAuth();
  }, []);

  const login = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // GIS opens a popup → user consents → we get the token directly
      const tokenResponse = await googleAuthService.requestAccessToken();

      if (!tokenResponse.access_token) {
        throw new Error('No se recibió token de acceso');
      }

      // Get user info with the token
      const userInfo = await googleAuthService.getUserInfo(tokenResponse.access_token);

      const user: User = {
        id: userInfo.id,
        email: userInfo.email,
        name: userInfo.name,
        picture: userInfo.picture,
      };

      // Save tokens and user
      await tokenManager.saveTokens({
        accessToken: tokenResponse.access_token,
        expiresAt: Date.now() + tokenResponse.expires_in * 1000,
      });
      await tokenManager.saveUser(user);

      // --- CANDADO: solo correos autorizados pueden entrar ---
      // Pedimos la hoja del vendedor una vez. El Apps Script revisa la pestana
      // "Autorizados" del Registro Maestro: si el correo no esta, responde
      // NO_AUTORIZADO y lo atrapamos aqui para regresarlo al login.
      try {
        await sheetsService.getOrCreateVendorSheet(user.email, user.name);
      } catch (authError) {
        const msg = authError instanceof Error ? authError.message : '';
        if (msg.includes('NO_AUTORIZADO')) {
          // No autorizado: limpiamos la sesion y mostramos el mensaje amable.
          await tokenManager.clearAll();
          setState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: MENSAJE_NO_AUTORIZADO,
          });
          return;
        }
        // Cualquier otro problema (red, Apps Script caido, etc.) NO debe impedir
        // el acceso: dejamos entrar y la app reintentara al cargar datos.
        console.warn('[Auth] No se pudo verificar la hoja al iniciar sesión (se permite continuar):', authError);
      }

      setState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      // If user just closed the popup, don't show error
      if (error instanceof Error && error.message === 'popup_closed') {
        setState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      console.error('Login error:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Error de autenticación',
      }));
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      const tokens = await tokenManager.getTokens();
      if (tokens?.accessToken) {
        await googleAuthService.revokeToken(tokens.accessToken).catch(() => {
          // Ignore revoke errors — token may already be expired
        });
      }
    } catch {
      // Ignore
    }

    await tokenManager.clearAll();
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
